import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useSegmentoVisao } from '@/contexts/SegmentoVisaoContext';
import { toast } from '@/hooks/use-toast';

// Controle da portabilidade no PRÉ-ATIVO (Douglas 24/09/2026). Rota própria no
// backend: a taxa do mês, o que cada linha já viveu na fila (régua do SMS,
// reenvios, aviso da véspera, o que a operadora diz, as tratativas) e as ações
// que mandam mensagem — sempre pela conversa do pedido.

async function requisitar<T>(path: string, body?: unknown): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Sua sessão expirou. Entre novamente.');
  const base = ((import.meta.env.VITE_SUPABASE_URL as string) || 'https://beta.crm-api.primetecia.com.br/api').replace(/\/$/, '');
  let r: Response;
  try {
    r = await fetch(`${base}/v1/portabilidade/${path}`, {
      method: body === undefined ? 'GET' : 'POST',
      headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch {
    throw new Error('Não foi possível conectar ao CRM. Nada foi alterado.');
  }
  const result = await r.json().catch(() => ({ error: 'O CRM não respondeu corretamente.' }));
  if (!r.ok) throw new Error(result.error || 'Não foi possível concluir.');
  return result.data as T;
}

export interface TaxaMes {
  mes: string; portadas_mes: number; portadas_faturamento: number;
  entrou: number; portou: number; morreu: number; andando: number;
  taxa: number | null; dias_mediana: number | null;
}

/** Tile PORTABILIDADE da Visão Prime: portadas no mês + taxa da coorte. */
export function usePortabilidadeTaxa(mesReferencia: string, enabled = true) {
  const tipoPessoa = useSegmentoVisao();
  const mes = (mesReferencia || '').slice(0, 7);
  return useQuery({
    queryKey: ['portabilidade-taxa', tipoPessoa, mes],
    enabled: enabled && mes.length === 7,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    queryFn: () => requisitar<{ meta: number; atual: TaxaMes; anterior: TaxaMes }>(
      `taxa?mes=${mes}&tipo_pessoa=${tipoPessoa}`),
  });
}

export interface ExtrasPortabilidade {
  item_id: string;
  entrou_pre_ativo_em: string | null;
  entrou_coluna_em: string | null;
  sms_msg1_em: string | null;
  sms_msg2_em: string | null;
  sms_cobranca_em: string | null;
  reenvios: number;
  ultimo_reenvio_em: string | null;
  aviso_vespera_em: string | null;
  aviso_vespera_status: 'enviando' | 'enviado' | 'falhou' | 'incerto' | null;
  /** Aviso da instalação de fibra, lido da timeline (agendada/reagendada/véspera). */
  aviso_instalacao: { status: 'enviando' | 'enviado' | 'falhou' | 'incerto' | null; em: string | null; tipo: string } | null;
  operadora: { resumo: string; claro: boolean | null; em: string } | null;
  tratativas: number;
  ultima_tratativa: { motivo: string | null; acao: string | null; texto: string; em: string; por: string | null } | null;
  /** A última mensagem das conversas do cliente é dele: desde quando espera resposta. */
  cliente_esperando_desde?: string | null;
  /** O último botão da consulta na Claro desde que a linha entrou na fila SMS Enviado. */
  resultado?: ResultadoConsulta | null;
  /** A conversa do pedido no Inbox. */
  conversa_id?: string | null;
}

export interface ResultadoConsulta {
  tipo: 'sms_reenvio' | 'titularidade' | 'erro_bilhete';
  em: string;
  por: string | null;
  tentativa: number | null;
  contato: string | null;
  desde: string | null;
  resposta_em: string | null;
}

export type ContatoTitularidade = 'liguei' | 'mensagem' | 'respondeu';
export type AcaoDaLinha = 'sms' | 'titularidade' | 'bilhete' | 'cancelada';

export interface PreviaDaLinha {
  texto: string;
  conversa_id?: string | null;
  /** Titularidade: o conflito já foi mandado ao cliente e segue aberto. */
  aberta?: boolean;
  desde?: string | null;
  /** Erro no bilhete: o e-mail. */
  assunto?: string;
  para?: string[];
  numero?: number;
}

/** O que a linha já viveu no Pré-Ativo, por item. */
export function usePortabilidadeExtras(itemIds: string[], enabled = true) {
  const chave = [...itemIds].sort().join(',');
  return useQuery({
    queryKey: ['portabilidade-extras', chave],
    enabled: enabled && itemIds.length > 0,
    staleTime: 30_000,
    refetchInterval: enabled ? 60_000 : false,
    queryFn: async () => {
      const rows = await requisitar<ExtrasPortabilidade[]>('triagem/extras', { itemIds });
      return new Map(rows.map(r => [r.item_id, r]));
    },
  });
}

export interface EventoHistorico {
  tipo: string; em: string; coluna: string | null;
  motivo: string | null; acao: string | null; texto: string; por: string | null;
}

export function usePortabilidadeHistorico(itemId: string | null) {
  return useQuery({
    queryKey: ['portabilidade-historico', itemId],
    enabled: !!itemId,
    queryFn: () => requisitar<EventoHistorico[]>(`itens/${itemId}/historico`),
  });
}

export interface OpcaoFila { tipo: 'motivo' | 'acao'; codigo: string; rotulo: string }

export function useOpcoesDaFila(coluna: string, enabled = true) {
  return useQuery({
    queryKey: ['fila-opcoes', coluna],
    enabled,
    staleTime: 30 * 60_000,
    queryFn: () => requisitar<OpcaoFila[]>(`opcoes?coluna=${encodeURIComponent(coluna)}`),
  });
}

function invalidarTriagens(queryClient: ReturnType<typeof useQueryClient>) {
  ['portabilidade-extras', 'portabilidade-historico', 'portabilidade-previa', 'triagem-portabilidade', 'triagem-portabilidade-concluida',
    'triagem-cpf-divergente', 'kanban-optimized', 'timeline-pedido-modal']
    .forEach(key => queryClient.invalidateQueries({ queryKey: [key] }));
}

/** O texto que o botão da linha vai mandar, para a BKO ver antes de apertar. */
export function usePreviaDaLinha(itemId: string | null, acao: AcaoDaLinha) {
  return useQuery({
    queryKey: ['portabilidade-previa', itemId, acao],
    enabled: !!itemId,
    staleTime: 0,
    queryFn: () => requisitar<PreviaDaLinha>(`itens/${itemId}/previa?acao=${acao}`),
  });
}

const erroDe = (e: unknown) => (e instanceof Error ? e.message : 'Tente novamente.');

/** SMS sem resposta: reenviei pelo forms da Claro. Avisa o cliente desta linha. */
export function useReenviarSms() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, motivo }: { itemId: string; motivo: string }) =>
      requisitar<{ tentativa: number; linhas: number }>(`itens/${itemId}/reenviar-sms`, { motivo }),
    onSuccess: (r) => {
      toast({ title: 'SMS sem resposta registrado', description: `Tentativa ${r.tentativa}: o cliente foi avisado e a linha foi para o fim da fila.` });
      invalidarTriagens(queryClient);
    },
    onError: (e) => toast({ title: 'Nada foi registrado', description: erroDe(e), variant: 'destructive' }),
  });
}

/** Conflito de titularidade: a 1ª vez manda ao cliente; nas voltas, a BKO diz como falou com ele. */
export function useTitularidadeSms() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, contato, nota }: { itemId: string; contato: ContatoTitularidade | null; nota: string }) =>
      requisitar<{ primeira: boolean; conversa_id: string | null }>(`itens/${itemId}/titularidade`, { contato, nota }),
    onSuccess: (r) => {
      toast({
        title: r.primeira ? 'Mensagem enviada ao cliente' : 'Contato registrado',
        description: 'A linha fica na fila com o selo titularidade e volta em 24 horas.',
      });
      invalidarTriagens(queryClient);
    },
    onError: (e) => toast({ title: 'Nada foi registrado', description: erroDe(e), variant: 'destructive' }),
  });
}

/** Erro no bilhete: e-mail aos gerentes da Claro pela atendimento@. */
export function useErroBilhete() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => requisitar<{ numero: number; assunto: string }>(`itens/${itemId}/erro-bilhete`, {}),
    onSuccess: (r) => {
      toast({ title: `${r.numero}º e-mail enviado à Claro`, description: 'A linha fica aguardando a Claro e volta em 24 horas.' });
      invalidarTriagens(queryClient);
    },
    onError: (e) => toast({ title: 'O e-mail não saiu', description: erroDe(e), variant: 'destructive' }),
  });
}

/** Linha cancelada ou desativada: depois de cancelar, pergunta ao cliente se quer linha nova. */
export function useAvisarLinhaCancelada() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => requisitar<{ avisado: boolean }>(`itens/${itemId}/avisar-linha-cancelada`, {}),
    onSuccess: () => {
      toast({ title: 'Cliente avisado', description: 'Perguntamos se ele quer uma linha nova da Claro no lugar.' });
      invalidarTriagens(queryClient);
    },
    onError: (e) => toast({ title: 'A linha foi cancelada, mas o cliente não foi avisado', description: erroDe(e), variant: 'destructive' }),
  });
}

export function usePedirTitularidade() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => requisitar<{ linhas: number }>(`itens/${itemId}/pedir-titularidade`, {}),
    onSuccess: () => {
      toast({ title: 'Mensagem enviada', description: 'Pedido de titularidade enviado pela conversa do pedido e registrado como tratativa.' });
      invalidarTriagens(queryClient);
    },
    onError: (e) => toast({ title: 'Mensagem não enviada', description: e instanceof Error ? e.message : 'Tente novamente.', variant: 'destructive' }),
  });
}

/** Registra a tratativa (por que está errado + o que está sendo feito). */
export function useRegistrarTratativa() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (p: { itemId: string; pedidoId: string; motivo: string; acao: string; texto: string }) => {
      if (p.texto.trim().length < 10) throw new Error('Descreva em uma frase o que foi visto e o que foi feito.');
      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await supabase.from('fila_justificativas').insert({
        item_id: p.itemId, pedido_id: p.pedidoId, coluna: '', justificativa: p.texto.trim(),
        usuario_id: user?.id, motivo_codigo: p.motivo, acao_codigo: p.acao,
      } as never);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: 'Tratativa registrada', description: 'Está na timeline do pedido.' });
      invalidarTriagens(queryClient);
    },
    onError: (e) => toast({ title: 'Tratativa não registrada', description: e instanceof Error ? e.message : 'Tente novamente.', variant: 'destructive' }),
  });
}

/** Dias corridos desde uma data ISO. */
export function diasDesde(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : Math.max(0, Math.floor((Date.now() - t) / 86_400_000));
}

export function horasDesde(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : Math.max(0, (Date.now() - t) / 3_600_000);
}
